import { defineStore, storeToRefs } from 'pinia'
import { setCSSVariablesWhitelabel } from './helpers/app-config'
import { isUnifiedCommerce } from '@shared/constants'
import { getTXTs, parseTXTs } from '@shared/plugins/dns-config'
import { computed, ref } from 'vue'
import { useLogException } from '@shared/composables/use-log-exception'

import unzerColorLogo from '@shared/assets/images/unzer-logo-color.svg'
import tillhubColorLogo from '@shared/assets/images/th-logo-color.svg'
import tillhubLogo from '@shared/assets/images/th-logo.svg'
import get from 'just-safe-get'
import { useConfigurationStore } from './configuration'

const validThemes = ['unzer', 'tillhub']
type BrandingTheme = (typeof validThemes)[number]

export const useWhitelabelThemeStore = defineStore('whitelabelTheme', () => {
  const { logException } = useLogException()
  const { tenantConfig } = storeToRefs(useConfigurationStore())

  const baseTheme = computed<BrandingTheme>(() => {
    // Check for branding override.
    const branding = get(tenantConfig.value, 'themes.dashboard.branding')

    if (validThemes.includes(branding)) {
      return branding
    }
    // Return default branding.
    return isUnifiedCommerce() ? 'unzer' : 'tillhub'
  })

  const customWallpaper = ref<string | undefined>(undefined)
  const customBGColor = ref<string | undefined>(undefined)
  const customWelcomeBGColor = ref<string | undefined>(undefined)
  const customWelcomeLogo = ref<string | undefined>(undefined)
  const customLogo = ref<string | undefined>(undefined)

  const fetchDNSConfig = async () => {
    // Do not allow override for unified commerce.
    if (isUnifiedCommerce()) {
      return
    }
    try {
      const txts = await getTXTs()
      if (!txts) {
        return
      }

      const txtConfig = parseTXTs(txts)
      if (!txtConfig) {
        return
      }

      customLogo.value = txtConfig.dashboard_logo
      customWelcomeLogo.value = txtConfig.dashboard_welcome_logo
      customWelcomeBGColor.value = txtConfig.dashboard_welcome_bg_color
      customWallpaper.value = txtConfig.dashboard_welcome_bg_image
      customBGColor.value = txtConfig.dashboard_bg_color

      // Replace css variables
      setCSSVariablesWhitelabel(txtConfig.css_variables)
    } catch (error) {
      logException(error, { trackError: false })
    }
  }

  const defaultLogo = computed(() => {
    return baseTheme.value === 'unzer' ? unzerColorLogo : tillhubLogo
  })

  const defaultWelcomeLogo = computed(() => {
    return baseTheme.value === 'unzer' ? unzerColorLogo : tillhubColorLogo
  })

  const logo = computed(() => {
    return customLogo.value || defaultLogo.value
  })

  const welcomeLogo = computed(() => {
    return (
      customWelcomeLogo.value || customLogo.value || defaultWelcomeLogo.value
    )
  })

  const $reset = () => {
    customWallpaper.value = undefined
    customBGColor.value = undefined
    customWelcomeBGColor.value = undefined
    customWelcomeLogo.value = undefined
    customLogo.value = undefined
  }

  return {
    logo,
    welcomeLogo,
    themeClass: baseTheme,
    customWallpaper,
    customBGColor,
    customWelcomeBGColor,
    customWelcomeLogo,
    customLogo,
    globalReset: false, // Disable global reset for this store.
    fetchDNSConfig,
    $reset
  }
})
